import { mdiExportVariant } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Capacitor } from "@capacitor/core";

import LocalStorage from "services/LocalStorage";
import { getNotification, isIOS, isInStandaloneMode, supportNotification } from "utils/device";
import { oneSignalShowNativePrompt } from "services/OneSignal";
import useBoundStore from "store";
import styles from "./notificationDialog.module.scss";

export interface NotificationDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function NotificationDialog(props: NotificationDialogProps): JSX.Element {
  const { open, onClose } = props;
  const setNotification = useBoundStore((state) => state.notification.set);
  const localStorage = LocalStorage.getInstance();
  let dialogContent: React.ReactNode | string = (
    <>
      To start receiving notifications for new events and friend requests, simply tap <b>Allow</b>. You can also enable
      this feature later in your settings.
    </>
  );
  let allowBtnText: string | null = "Allow";
  let cancelBtnText = "Cancel";

  if (!Capacitor.isNativePlatform() && getNotification()?.permission === "denied") {
    dialogContent = "To receive notifications, you must allow them from the browser settings for this website.";
    allowBtnText = null;
    cancelBtnText = "Close";
  }

  if (!supportNotification()) {
    dialogContent = "Your device does not support notifications.";
    allowBtnText = null;
    cancelBtnText = "Close";
  }

  if (isIOS() && supportNotification() && !isInStandaloneMode()) {
    dialogContent = (
      <span>
        To receive notifications you must add GroupUp to your home screen. To do this, press on
        <Icon className={styles.exportVariantIcon} path={mdiExportVariant} size={0.75} />
        and then &#39;Add to Home Screen&#39;.
      </span>
    );
    allowBtnText = null;
    cancelBtnText = "Close";
  }

  async function handleAllow() {
    oneSignalShowNativePrompt().then((granted) => setNotification({ optedIn: granted }));
    localStorage.setNotificationDialogShown(true);
    onClose();
  }

  async function handleClose() {
    localStorage.setNotificationDialogShown(true);
    onClose();
  }

  return (
    <Dialog onClose={() => undefined} open={open} maxWidth='xs'>
      <DialogTitle>🔔 Enable notifications</DialogTitle>
      <DialogContent>{dialogContent}</DialogContent>
      <DialogActions>
        {cancelBtnText ? (
          <Button onClick={handleClose} color='primary'>
            {cancelBtnText}
          </Button>
        ) : null}
        {allowBtnText ? (
          <Button onClick={handleAllow} color='primary' variant='contained'>
            {allowBtnText}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
